import {
  PropertyObjectNames,
  PropertyObjectNamesType,
} from "./Model/FeatureTypes";

export const getPropertyObjectContextName = (
  type: PropertyObjectNamesType = "Object"
): string => PropertyObjectNames[type].split(".").pop() as string;

export const getCleanCommaDelimitedList = (value: any = ""): string[] => {
  const cleanValue = typeof value === "string" ? value.trim() : "";

  return cleanValue
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
};

export type BooleanPropBuilder<PropsType extends Record<string, any>> = (
  userData: Record<string, any>
) => Partial<PropsType>;

export const getBooleanPropBuilder =
  <PropsType extends Record<string, any>>(
    contextName: string,
    props: Partial<PropsType>
  ): BooleanPropBuilder<PropsType> =>
  (userData = {}) => {
    const { [contextName]: value } = userData;

    return !!value ? props : {};
  };

export const getContexts = (userData: Record<string, any> = {}): string[] =>
  Object.keys(userData);

export const getContextPresets = (
  userData: Record<string, any> = {},
  presetMap: Record<
    string,
    | Record<string, any>
    | ((userData: Record<string, any>) => Record<string, any>)
  >
): Record<string, any> =>
  getContexts(userData).reduce(
    (acc, context) => ({
      ...acc,
      ...(presetMap[context] instanceof Function
        ? (presetMap[context] as Function)(userData)
        : presetMap[context]),
    }),
    {}
  );

export const getUserDataProperties = (
  userData: Record<string, any> = {},
  type: PropertyObjectNamesType = "Object"
) => userData[getPropertyObjectContextName(type)] || {};

export const applyContextPresets = <
  TargetType extends object | null | undefined
>(
  target: TargetType,
  userData: Record<string, any> = {},
  presetMap: Record<
    string,
    | Record<string, any>
    | ((userData: Record<string, any>) => Record<string, any>)
  >,
  userDataType: PropertyObjectNamesType = "Object"
): TargetType => {
  if (target) {
    Object.assign(
      target,
      getContextPresets(
        getUserDataProperties(userData, userDataType),
        presetMap
      )
    );
  }

  return target;
};

export const userDataHasAnyContext = (
  userData: Record<string, any> = {},
  contexts: string[] = [],
  userDataType: PropertyObjectNamesType = "Object"
): boolean =>
  getContexts(getUserDataProperties(userData, userDataType)).some((context) =>
    contexts.includes(context)
  );
